import { useState, useMemo, useCallback } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config";
import MultiSelectDropdown from "../../../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import TagFilterBar from "../../../../../Settings/Components/TagFilterBar";
import { questionMatchesSteps } from "../../../../../Settings/Components/TagFilterUtils";

const T = {
  blue: "#2563eb", blueL: "#eff6ff", blueB: "#bfdbfe",
  green: "#15803d", greenL: "#f0fdf4", greenB: "#bbf7d0",
  amber: "#b45309", amberL: "#fffbeb", amberB: "#fde68a",
  red: "#be123c", redL: "#fff1f2", redB: "#fecdd3",
  ink: "#0f172a", ink2: "#334155", ink3: "#64748b", ink4: "#94a3b8",
  rule: "#e2e8f0", bg: "#f8fafc", white: "#fff",
};

const Spin = ({ size = 12, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "od-spin .6s linear infinite" }} />
);

/**
 * direction="location": scopeId is a locationId. Rows = the users currently
 *   assigned at this location for each qid — one row per (qid, user).
 *
 * direction="user": scopeId is a userId. Rows = the locations this user is
 *   assigned to for each qid — one row per (qid, location).
 *
 * Both directions render the same shape: an independently checkable +
 * removable row per (qid, userId, locationId) triple. The unassign endpoint
 * is (assignmentIds, userId, locationId) scoped, so a single user@location
 * can always be detached without touching that user's other locations (or,
 * in the location view, other users at the same location).
 */
const OwnerAssignedKpisDrawer = ({
  direction,           // "location" | "user"
  scopeId,
  scopeLabel,
  qIds,
  mode,                // "unassigned" | "assignments"
  questionTitles,
  dbAssignments,
  locationNameById = {},
  allQuestions = [],
  financialYearId,
  onClose,
  onRefreshAssignments,
}) => {
  const isReadOnly = mode === "unassigned";
  const [search, setSearch] = useState("");
  const [subFilter, setSubFilter] = useState([]); // userFilter (location dir) or locationFilter (user dir)
  const [filterSteps, setFilterSteps] = useState([]);
  const [removalError, setRemovalError] = useState("");
  const [removingKeys, setRemovingKeys] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  // qid → raw tag-combo data, for the cascade tag filter below
  const tagsByQid = useMemo(() => {
    const map = {};
    allQuestions.forEach((q) => { map[q.questionId] = q.tags; });
    return map;
  }, [allQuestions]);

  // Tag filter options should reflect only the KPIs actually listed in this
  // drawer (this scope's unassigned/assignment qIds) — not the full KPI catalog.
  const filterableQuestions = useMemo(() => {
    const idSet = new Set(qIds.map(String));
    return allQuestions.filter((q) => idSet.has(String(q.questionId)));
  }, [allQuestions, qIds]);

  const getAssignees = useCallback((qid) => {
    const item = dbAssignments.find((d) => (d.questionId ?? d.assignmentId) === qid);
    return item?.assignees || [];
  }, [dbAssignments]);

  /* Rows, unified across both directions: one row per (qid, userId, locId).
     "location" direction fixes locId=scopeId and varies userId; "user"
     direction fixes userId=scopeId and varies locId. */
  const rowsByQid = useMemo(() => {
    if (isReadOnly) return {};
    const map = {};

    /* Scope ids are `sub_<id>` for a sub-location, else the location id.
       Assignees expose `scopes` (location + sub-location pairs); older payloads
       only carry location ids, which can only mean a whole-site assignment. */
    const parseScopeId = (id) => {
      const raw = String(id);
      return raw.startsWith("sub_")
        ? { subLocationId: Number(raw.slice(4)), locationId: null }
        : { subLocationId: null, locationId: Number(raw) };
    };
    const scopeIdOf = (s) => (s.subLocationId ? `sub_${s.subLocationId}` : String(s.locationId));
    const assigneeScopeIds = (a) => (
      Array.isArray(a?.scopes) && a.scopes.length
        ? a.scopes.map(scopeIdOf)
        : (a?.locationIds || a?.sourceIds || []).map(String)
    );

    qIds.forEach((qid) => {
      if (direction === "location") {
        const target = String(scopeId);
        const assignees = getAssignees(qid).filter((a) => assigneeScopeIds(a).includes(target));
        map[qid] = assignees.map((a) => ({
          userId: String(a.userId),
          locId: target,
          ...parseScopeId(target),
          qid,
          name: [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email || `User #${a.userId}`,
          key: `${qid}-${a.userId}-${target}`,
        }));
      } else {
        const a = getAssignees(qid).find((x) => String(x.userId) === String(scopeId));
        map[qid] = assigneeScopeIds(a).map((sid) => ({
          userId: String(scopeId),
          locId: sid,
          ...parseScopeId(sid),
          qid,
          name: locationNameById[sid] || `#${sid}`,
          key: `${qid}-${scopeId}-${sid}`,
        }));
      }
    });
    return map;
  }, [isReadOnly, qIds, direction, scopeId, getAssignees, locationNameById]);

  // subFilter narrows by user (location direction) or by location (user direction)
  const subFilterKey = direction === "location" ? "userId" : "locId";

  const subFilterOptions = useMemo(() => {
    if (isReadOnly) return [];
    const map = new Map();
    Object.values(rowsByQid).forEach((rows) => rows.forEach((r) => {
      if (!map.has(r[subFilterKey])) map.set(r[subFilterKey], r.name);
    }));
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [isReadOnly, rowsByQid, subFilterKey]);

  const filteredQIds = useMemo(() => qIds.filter((qid) => {
    const title = (questionTitles[qid] || `KPI #${qid}`).toLowerCase();
    if (search && !title.includes(search.toLowerCase())) return false;
    if (filterSteps.length && !questionMatchesSteps({ tags: tagsByQid[qid], questionId: qid }, filterSteps)) return false;
    if (!isReadOnly && subFilter.length) {
      return (rowsByQid[qid] || []).some((r) => subFilter.includes(r[subFilterKey]));
    }
    return true;
  }), [qIds, questionTitles, search, filterSteps, tagsByQid, subFilter, isReadOnly, rowsByQid, subFilterKey]);

  const visibleRows = useMemo(() => {
    if (isReadOnly) return [];
    const list = [];
    filteredQIds.forEach((qid) => {
      (rowsByQid[qid] || []).forEach((r) => {
        if (subFilter.length && !subFilter.includes(r[subFilterKey])) return;
        list.push(r);
      });
    });
    return list;
  }, [isReadOnly, filteredQIds, rowsByQid, subFilter, subFilterKey]);

  const allSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedKeys.has(r.key));

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };
  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? new Set() : new Set(visibleRows.map((r) => r.key)));
  };

  /* Group by (userId, locId) before calling the endpoint — each pair maps to
     exactly one unassign call scoped by both, so removing user A from
     location X never touches user A's other locations (or, in the location
     view, other users still assigned at that same location). */
  const performRemovals = useCallback(async (items) => {
    if (!items.length) return;
    setRemovalError("");
    setRemovingKeys(new Set(items.map((it) => it.key)));
    setBulkBusy(items.length > 1);
    try {
      const byUserLoc = new Map();
      items.forEach((it) => {
        const groupKey = `${it.userId}-${it.locId}`;
        if (!byUserLoc.has(groupKey)) {
          byUserLoc.set(groupKey, {
            userId: it.userId,
            locationId: it.locationId,
            subLocationId: it.subLocationId,
            qids: [],
          });
        }
        byUserLoc.get(groupKey).qids.push(it.qid);
      });
      for (const { userId, locationId, subLocationId, qids } of byUserLoc.values()) {
        await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/assigned-kpis/unassign`, {},
          {
            financialYearId,
            assignmentIds: [...new Set(qids)],
            userId: Number(userId),
            // A sub-location row knows only its own id; the parent is resolved
            // server-side from it, and omitting subLocationId means whole site.
            locationId: locationId ? Number(locationId) : undefined,
            subLocationId: subLocationId ? Number(subLocationId) : undefined,
          },
          "POST"
        );
      }
      await onRefreshAssignments?.();
      setSelectedKeys((prev) => { const n = new Set(prev); items.forEach((it) => n.delete(it.key)); return n; });
    } catch (e) {
      setRemovalError(e?.message || "Failed to remove assignment(s).");
    } finally {
      setRemovingKeys(new Set());
      setBulkBusy(false);
    }
  }, [onRefreshAssignments, financialYearId]);

  const handleRemoveSelected = () => performRemovals(visibleRows.filter((r) => selectedKeys.has(r.key)));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex" }}>
      <style>{`@keyframes od-spin { to { transform: rotate(360deg); } }`}</style>
      <div onClick={onClose} style={{ flex: 1, background: "rgba(0,0,0,.35)", cursor: "pointer" }} />
      <div style={{ width: 540, maxWidth: "95vw", background: T.white, display: "flex", flexDirection: "column", boxShadow: "-8px 0 40px rgba(0,0,0,.18)" }}>

        <div style={{ padding: "16px 18px 13px", borderBottom: `1px solid ${T.rule}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>
                {scopeLabel} · {mode === "unassigned" ? "Unassigned KPIs" : "Assignments"}
              </div>
              <div style={{ fontSize: 11, color: T.ink3, marginTop: 2 }}>
                {filteredQIds.length} of {qIds.length} KPI{qIds.length !== 1 ? "s" : ""} shown{isReadOnly ? " · Read-only" : ""}
              </div>
            </div>
            <button onClick={onClose} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 30, height: 30, cursor: "pointer", fontSize: 15, color: T.ink3 }}>×</button>
          </div>

          <div style={{ marginTop: 10 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search KPIs…"
              style={{ width: "100%", padding: "7px 10px", fontSize: 12, border: `1px solid ${T.rule}`, borderRadius: 7, background: T.bg, boxSizing: "border-box" }} />
          </div>

          {/* KPI tag filter (both modes) + Users filter (assignments mode only) — same row */}
          <div style={{ marginTop: 8, display: "flex", gap: 7, alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TagFilterBar
                allQuestions={filterableQuestions}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
                pendingIds={new Set(filteredQIds)}
              />
            </div>
            {!isReadOnly && (
              <div style={{ marginTop: 8 }}>
                <MultiSelectDropdown
                  options={subFilterOptions}
                  selected={subFilter}
                  onChange={setSubFilter}
                  placeholder={direction === "location" ? "Filter by user…" : "Filter by location…"}
                  showSelectAll={false}
                />
              </div>
            )}
          </div>
        </div>

        {!isReadOnly && removalError && (
          <div style={{ margin: "10px 14px 0", padding: "8px 12px", borderRadius: 7, background: T.redL, border: `1px solid ${T.redB}`, color: T.red, fontSize: 11.5 }}>
            ⚠ {removalError}
          </div>
        )}

        {!isReadOnly && visibleRows.length > 0 && (
          <div style={{ margin: "10px 14px 0", padding: "7px 11px", borderRadius: 7, background: T.bg, border: `1px solid ${T.rule}`, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ accentColor: T.blue, width: 13, height: 13 }} />
              Select all ({visibleRows.length})
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={handleRemoveSelected} disabled={selectedKeys.size === 0 || bulkBusy}
                style={{ fontSize: 11, fontWeight: 700, color: selectedKeys.size ? T.red : T.ink4, background: T.white, border: `1.5px solid ${selectedKeys.size ? T.redB : T.rule}`, borderRadius: 7, padding: "5px 10px", cursor: selectedKeys.size && !bulkBusy ? "pointer" : "not-allowed" }}>
                Remove Selected{selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ""}
              </button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
          {filteredQIds.length === 0 && <div style={{ textAlign: "center", color: T.ink4, fontSize: 12, padding: "30px 0" }}>No KPIs match your filters.</div>}

          {filteredQIds.map((qid, i) => {
            const title = questionTitles[qid] || `KPI #${qid}`;

            if (isReadOnly) {
              return (
                <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, flex: 1 }}>{title}</span>
                  <span style={{ fontSize: 10, color: T.amber, background: T.amberL, padding: "2px 8px", borderRadius: 20, border: `1px solid ${T.amberB}`, whiteSpace: "nowrap" }}>Needs owner</span>
                </div>
              );
            }

            const rows = (rowsByQid[qid] || []).filter((r) => !subFilter.length || subFilter.includes(r[subFilterKey]));
            if (rows.length === 0) return null; // fully filtered out

            return (
              <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, marginBottom: 7 }}>{title}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {rows.map((r) => {
                    const isRemoving = removingKeys.has(r.key);
                    const isSelected = selectedKeys.has(r.key);
                    return (
                      <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 9px", borderRadius: 6, background: T.blueL, border: `1px solid ${T.blueB}` }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(r.key)} style={{ accentColor: T.blue, width: 12, height: 12, flexShrink: 0 }} />
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.ink2, flex: 1 }}>
                          {direction === "location" ? r.name : `📍 ${r.name}`}
                        </span>
                        <button onClick={() => performRemovals([r])} disabled={isRemoving || bulkBusy}
                          style={{ border: `1px solid ${T.redB}`, background: T.white, color: T.red, borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: (isRemoving || bulkBusy) ? "not-allowed" : "pointer", flexShrink: 0 }}>
                          {isRemoving ? <Spin size={9} color={T.red} /> : "Remove"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.rule}` }}>
          <button onClick={onClose} style={{ width: "100%", padding: "8px 0", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerAssignedKpisDrawer;